import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Button,
  Stack,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { translations } from './ociaRegistrationTranslations';

const emptyAddress = () => ({ street: '', city: '', state: '', zip: '', country: '' });

const initialForm = () => ({
  // dateFilled is intentionally omitted — the server auto-stamps it on submission.
  firstName: '', middleName: '', lastName: '', maidenName: '',
  dateOfBirth: '',
  placeOfBirth: { city: '', stateProvince: '', postalCode: '', country: '' },
  fatherName: '', motherName: '', motherMaidenName: '',
  isStudent: false,
  homeAddress: emptyAddress(),
  schoolAddress: emptyAddress(),
  cellPhone: '', occupation: '', email: '',
  religiousHistory: {
    presentAffiliation: '',
    everBaptized: '',
    baptism: { denomination: '', dateOrAge: '', baptismalName: '', churchName: '', churchAddress: '', location: '' },
    catholicSacramentsReceived: []
  },
  maritalStatus: {
    status: '',
    registrant: { marriedBefore: false, previousMarriages: [] },
    partner: { name: '', affiliation: '', marriedBefore: false, previousMarriages: [] },
    marriage: { dateOfMarriage: '', placeOfMarriage: '', officiatingAuthority: '' },
    widowedPreviousSpouseStillLiving: false
  },
  faithJourney: { whatLedYou: '', religiousEducationReceived: '', contactWithChurch: '', questionsConcerns: '' },
  intent: '',
  signature: { name: '', date: new Date().toISOString().slice(0, 10) }
});

const NEEDS_MARRIAGE = ['married', 'separated', 'divorced', 'widowed'];
const NEEDS_PARTNER = ['engaged', 'married', 'separated', 'divorced', 'widowed'];
// Statuses that get the "previously married" questions (widowed is intentionally excluded).
const NEEDS_PREV_MARRIAGE_QS = ['engaged', 'married', 'separated', 'divorced'];

// These presentational helpers must stay at module scope. Defined inside the
// component they get a new identity on every render, so React unmounts and
// remounts their subtrees on each keystroke — dropping input focus after
// every character typed.
const YesNoRadio = ({ label, value, onChange, t }) => (
  <FormControl>
    <FormLabel>{label}</FormLabel>
    <RadioGroup row value={value ? 'yes' : 'no'} onChange={(e) => onChange(e.target.value === 'yes')}>
      <FormControlLabel value="yes" control={<Radio />} label={t.yes} />
      <FormControlLabel value="no" control={<Radio />} label={t.no} />
    </RadioGroup>
  </FormControl>
);

const Subsection = ({ title, children, last }) => (
  <Box sx={{ mb: last ? 0 : 3.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>{title}</Typography>
    {children}
  </Box>
);

const LanguageToggle = ({ light = false, t, language, setLang }) => {
  const idleSx = light ? { color: '#fff', borderColor: 'rgba(255,255,255,0.7)' } : {};
  const activeSx = light ? { bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', borderColor: 'transparent' } : {};
  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      {['en', 'es'].map((lng) => (
        <Chip
          key={lng}
          label={t.languageLabel[lng]}
          size="small"
          variant={language === lng ? 'filled' : 'outlined'}
          color={!light && language === lng ? 'primary' : 'default'}
          onClick={() => setLang(lng)}
          sx={language === lng ? activeSx : idleSx}
        />
      ))}
    </Stack>
  );
};

const AddressBlock = ({ value, base, t, setField }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}><TextField fullWidth label={t.street} value={value.street} onChange={(e) => setField(`${base}.street`, e.target.value)} /></Grid>
    <Grid item xs={12} sm={6}><TextField fullWidth label={t.city} value={value.city} onChange={(e) => setField(`${base}.city`, e.target.value)} /></Grid>
    <Grid item xs={6} sm={3}><TextField fullWidth label={t.state} value={value.state} onChange={(e) => setField(`${base}.state`, e.target.value)} /></Grid>
    <Grid item xs={6} sm={3}><TextField fullWidth label={t.zip} value={value.zip} onChange={(e) => setField(`${base}.zip`, e.target.value)} /></Grid>
    <Grid item xs={12} sm={6}><TextField fullWidth label={t.country} value={value.country} onChange={(e) => setField(`${base}.country`, e.target.value)} /></Grid>
  </Grid>
);

const OCIARegistration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [language, setLanguage] = useState(() => {
    try {
      const saved = window.localStorage.getItem('ociaRegistrationLanguage');
      return saved === 'es' ? 'es' : 'en';
    } catch { return 'en'; }
  });
  const t = translations[language];
  const setLang = (lang) => {
    setLanguage(lang);
    try { window.localStorage.setItem('ociaRegistrationLanguage', lang); } catch {}
  };

  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm());
  // UI-only: not submitted — an on-campus student is represented by a filled schoolAddress.
  const [livesOnCampus, setLivesOnCampus] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(''); // 'online' | 'cash'
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [squareReady, setSquareReady] = useState(false);
  const [squareCard, setSquareCard] = useState(null);
  // One payment idempotency key per logical submission: kept across retries so
  // a resubmit after an ambiguous failure can never charge the card twice.
  const paymentIdemKeyRef = useRef(null);

  // Fee calculation
  const computeAmountDue = () => {
    if (!settings) return 0;
    const base = Number(settings.registrationFee) || 0;
    const d = settings.discount;
    if (d?.enabled) {
      const notExpired = !d.endDate || new Date() <= new Date(d.endDate);
      if (notExpired) return Number(d.discountedRegistrationFee) || 0;
    }
    return base;
  };
  const amountDue = computeAmountDue();
  const discountActive = settings?.discount?.enabled && (!settings.discount.endDate || new Date() <= new Date(settings.discount.endDate));

  const decisionDateStr = (() => {
    if (!settings?.decisionDeadlineDate) return t.toBeDetermined;
    try {
      return new Date(settings.decisionDeadlineDate).toLocaleDateString(t.locale, { timeZone: 'America/New_York' });
    } catch { return t.toBeDetermined; }
  })();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/ocia/settings/status-public');
        setSettings(res.data);
      } catch (e) {
        console.error('Failed to load OCIA settings:', e);
        setSettings({ registrationEnabled: false });
      } finally {
        setLoadingSettings(false);
      }
    };
    load();
  }, []);

  const PAYMENT_STEP = 6;

  // Initialize Square when on payment step paying online with a charge due.
  useEffect(() => {
    if (activeStep !== PAYMENT_STEP) return;
    if (paymentMethod !== 'online') return;
    if (amountDue < 1) return;
    if (squareReady) return;

    let cancelled = false;
    const init = async () => {
      try {
        const configRes = await axios.get('/square-config');
        const { applicationId, locationId } = configRes.data || {};
        if (!applicationId || !locationId) throw new Error('Square configuration is incomplete.');

        const isProd = window.location.hostname.includes('stbridgetup.org');
        const sdkUrl = isProd
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';

        const ensureSdk = () => new Promise((resolve, reject) => {
          if (window.Square) return resolve();
          const existing = document.querySelector(`script[src="${sdkUrl}"]`);
          if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
          }
          const script = document.createElement('script');
          script.src = sdkUrl;
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        });

        await ensureSdk();
        if (cancelled) return;
        const payments = window.Square.payments(applicationId, locationId);
        const card = await payments.card();
        await card.attach('#ocia-reg-card-container');
        if (cancelled) { try { await card.destroy(); } catch (_) {} return; }
        setSquareCard(card);
        setSquareReady(true);
      } catch (e) {
        console.error('Square init failed:', e);
        setError(t.paymentSystemFailed);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [activeStep, paymentMethod, amountDue, squareReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = next;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) cur[k] = value;
        else { if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {}; cur = cur[k]; }
      });
      return next;
    });
  };

  const toggleSacrament = (value) => {
    setForm(prev => {
      const list = prev.religiousHistory.catholicSacramentsReceived || [];
      const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
      return { ...prev, religiousHistory: { ...prev.religiousHistory, catholicSacramentsReceived: next } };
    });
  };

  // `party` is 'registrant' or 'partner' — both keep an identical previousMarriages list.
  const addPrevMarriage = (party) => setForm(prev => ({
    ...prev,
    maritalStatus: {
      ...prev.maritalStatus,
      [party]: {
        ...prev.maritalStatus[party],
        previousMarriages: [...(prev.maritalStatus[party].previousMarriages || []), { name: '', affiliation: '', dateOfMarriage: '', placeOfMarriage: '', officiatingAuthority: '' }]
      }
    }
  }));
  const updatePrevMarriage = (party, i, k, v) => setForm(prev => {
    const pm = [...(prev.maritalStatus[party].previousMarriages || [])];
    pm[i] = { ...pm[i], [k]: v };
    return { ...prev, maritalStatus: { ...prev.maritalStatus, [party]: { ...prev.maritalStatus[party], previousMarriages: pm } } };
  });
  const removePrevMarriage = (party, i) => setForm(prev => ({
    ...prev,
    maritalStatus: { ...prev.maritalStatus, [party]: { ...prev.maritalStatus[party], previousMarriages: (prev.maritalStatus[party].previousMarriages || []).filter((_, idx) => idx !== i) } }
  }));

  const validateStep = () => {
    if (activeStep === 0) {
      return form.firstName.trim() && form.lastName.trim() && form.dateOfBirth;
    }
    if (activeStep === 1) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    }
    if (activeStep === 2) {
      return !!form.religiousHistory.everBaptized;
    }
    if (activeStep === 3) {
      return !!form.maritalStatus.status;
    }
    if (activeStep === 4) {
      return !!form.intent;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) {
      setError(t.requiredNotice);
      return;
    }
    setError(null);
    setActiveStep(s => Math.min(s + 1, PAYMENT_STEP));
  };
  const handleBack = () => {
    setError(null);
    setActiveStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!paymentMethod) {
      setError(t.howToPay);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        payment: { amount: amountDue, method: paymentMethod === 'online' ? 'online' : 'cash' }
      };

      // Online payment with a real charge: tokenize the card.
      if (paymentMethod === 'online' && amountDue >= 1) {
        if (!squareCard) {
          setError(t.paymentFailed);
          setSubmitting(false);
          return;
        }
        const tokenResult = await squareCard.tokenize();
        if (tokenResult.status !== 'OK') {
          const msg = (tokenResult.errors || []).map(e => e.message).join(', ') || t.cardDetailsInvalid;
          throw new Error(msg);
        }
        payload.squarePaymentToken = tokenResult.token;
        payload.paymentAmount = amountDue;
        if (!paymentIdemKeyRef.current) {
          paymentIdemKeyRef.current = window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        payload.paymentIdempotencyKey = paymentIdemKeyRef.current;
      }

      await axios.post('/ocia/public-submission', payload);
      setSubmitted(true);
    } catch (e) {
      // 400/402 mean the server rejected before charging, so a retry is a new
      // payment attempt and needs a fresh key. Any other failure is ambiguous —
      // keep the key so a resubmit cannot double-charge.
      const status = e?.response?.status;
      if (status === 400 || status === 402) {
        paymentIdemKeyRef.current = null;
      }
      setError(e?.response?.data?.message || e?.message || t.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  // Dev/test-only filler. Shown when ?test=1 is on the URL or running on localhost.
  const showTestData = (() => {
    try {
      const qp = new URLSearchParams(window.location.search).get('test');
      const host = window.location.hostname;
      return qp === '1' || host === 'localhost' || host === '127.0.0.1';
    } catch { return false; }
  })();

  const fillTestData = () => {
    setForm({
      ...initialForm(),
      firstName: 'Test', middleName: 'A', lastName: 'Candidate',
      dateOfBirth: '1990-05-15',
      placeOfBirth: { city: 'Camden', stateProvince: 'NJ', postalCode: '08102', country: 'USA' },
      fatherName: 'John Candidate', motherName: 'Mary Candidate', motherMaidenName: 'Smith',
      isStudent: false,
      homeAddress: { street: '123 Main St', city: 'Camden', state: 'NJ', zip: '08102', country: 'USA' },
      schoolAddress: emptyAddress(),
      cellPhone: '8565551234', occupation: 'Engineer', email: 'test.candidate@example.com',
      religiousHistory: {
        presentAffiliation: 'Baptist',
        everBaptized: 'yes',
        baptism: { denomination: 'Baptist', dateOrAge: 'Age 5', baptismalName: '', churchName: 'First Baptist Church', churchAddress: '456 Oak Ave', location: 'Camden, NJ' },
        catholicSacramentsReceived: []
      },
      maritalStatus: {
        status: 'married',
        registrant: { marriedBefore: true, previousMarriages: [{ name: 'Jane Prior', affiliation: 'None', dateOfMarriage: '2010-06-01', placeOfMarriage: 'City Hall, Camden', officiatingAuthority: 'civil' }] },
        partner: { name: 'Current Spouse', affiliation: 'Catholic', marriedBefore: true, previousMarriages: [{ name: 'Sam Former', affiliation: 'Methodist', dateOfMarriage: '2008-04-12', placeOfMarriage: 'Trenton, NJ', officiatingAuthority: 'christian-minister' }] },
        marriage: { dateOfMarriage: '2018-09-20', placeOfMarriage: 'St. Bridget University Parish', officiatingAuthority: 'catholic-cleric' },
        widowedPreviousSpouseStillLiving: false
      },
      faithJourney: {
        whatLedYou: 'My spouse and our family.',
        religiousEducationReceived: 'Sunday school as a child; none as an adult.',
        contactWithChurch: 'Attended Mass occasionally with my spouse.',
        questionsConcerns: 'Curious about the sacraments and the process.'
      },
      intent: 'fairly-sure',
      signature: { name: 'Test A Candidate', date: new Date().toISOString().slice(0, 10) }
    });
    setLivesOnCampus(false);
    setPaymentMethod('cash');
    setError(null);
  };

  // Repeatable previous-marriage cards, identical for the registrant and the spouse/fiancé(e).
  const renderPreviousMarriages = (party) => (
    <Box sx={{ mt: 1 }}>
      {(form.maritalStatus[party].previousMarriages || []).map((pm, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">{t.previousMarriage} {i + 1}</Typography>
            <IconButton size="small" color="error" onClick={() => removePrevMarriage(party, i)}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label={t.partnerName} value={pm.name} onChange={(e) => updatePrevMarriage(party, i, 'name', e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={t.partnerAffiliation} value={pm.affiliation} onChange={(e) => updatePrevMarriage(party, i, 'affiliation', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth type="date" label={t.dateOfMarriage} InputLabelProps={{ shrink: true }} value={pm.dateOfMarriage} onChange={(e) => updatePrevMarriage(party, i, 'dateOfMarriage', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label={t.placeOfMarriage} value={pm.placeOfMarriage} onChange={(e) => updatePrevMarriage(party, i, 'placeOfMarriage', e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label={t.officiatingAuthority} value={pm.officiatingAuthority} onChange={(e) => updatePrevMarriage(party, i, 'officiatingAuthority', e.target.value)}>
                {t.officiatingOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      ))}
      <Button startIcon={<AddIcon />} onClick={() => addPrevMarriage(party)} size="small">{t.addPreviousMarriage}</Button>
    </Box>
  );

  if (loadingSettings) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (settings && settings.registrationEnabled === false) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <LanguageToggle t={t} language={language} setLang={setLang} />
          <Typography variant="h5" gutterBottom>{t.closedTitle}</Typography>
          <Typography color="text.secondary">{t.closedBody}</Typography>
        </Paper>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom color="success.main">{t.successTitle}</Typography>
          <Typography sx={{ mb: 2 }}>{t.successBody}</Typography>
          {paymentMethod === 'cash' && amountDue > 0 && (
            <Alert severity="info">{t.successCashReminder}</Alert>
          )}
        </Paper>
      </Container>
    );
  }

  const status = form.maritalStatus.status;

  // Dynamic header — admin-editable in OCIA settings, falling back to translated defaults.
  const formTitle = (settings?.formTitle && settings.formTitle.trim()) || t.header;
  const formSubtitle = (settings?.formSubtitle && settings.formSubtitle.trim()) || t.intro;

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.personalTitle}</Typography>

            <Subsection title={t.nameSection}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}><TextField fullWidth required label={t.firstName} value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth label={t.middleName} value={form.middleName} onChange={(e) => setField('middleName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={4}><TextField fullWidth required label={t.lastName} value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t.maidenName} value={form.maidenName} onChange={(e) => setField('maidenName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth required type="date" label={t.dateOfBirth} InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={(e) => setField('dateOfBirth', e.target.value)} /></Grid>
              </Grid>
            </Subsection>

            <Subsection title={t.placeOfBirth}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}><TextField fullWidth label={t.city} value={form.placeOfBirth.city} onChange={(e) => setField('placeOfBirth.city', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label={t.stateProvince} value={form.placeOfBirth.stateProvince} onChange={(e) => setField('placeOfBirth.stateProvince', e.target.value)} /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth label={t.postalCode} value={form.placeOfBirth.postalCode} onChange={(e) => setField('placeOfBirth.postalCode', e.target.value)} /></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth label={t.country} value={form.placeOfBirth.country} onChange={(e) => setField('placeOfBirth.country', e.target.value)} /></Grid>
              </Grid>
            </Subsection>

            <Subsection title={t.parentsSection} last>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t.fatherName} value={form.fatherName} onChange={(e) => setField('fatherName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t.motherName} value={form.motherName} onChange={(e) => setField('motherName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label={t.motherMaidenName} value={form.motherMaidenName} onChange={(e) => setField('motherMaidenName', e.target.value)} /></Grid>
              </Grid>
            </Subsection>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.contactTitle}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label={t.email} value={form.email} onChange={(e) => setField('email', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t.cellPhone} value={form.cellPhone} onChange={(e) => setField('cellPhone', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t.occupation} value={form.occupation} onChange={(e) => setField('occupation', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}>
                <YesNoRadio
                  t={t}
                  label={t.areYouStudent}
                  value={form.isStudent}
                  onChange={(v) => {
                    setField('isStudent', v);
                    if (!v) {
                      setLivesOnCampus(false);
                      setField('schoolAddress', emptyAddress());
                    }
                  }}
                />
              </Grid>
              {form.isStudent && (
                <Grid item xs={12} sm={6}>
                  <YesNoRadio
                    t={t}
                    label={t.liveOnCampus}
                    value={livesOnCampus}
                    onChange={(v) => {
                      setLivesOnCampus(v);
                      if (!v) setField('schoolAddress', emptyAddress());
                    }}
                  />
                </Grid>
              )}
            </Grid>
            <Box sx={{ mt: 3.5 }}>
              <Subsection title={t.homeAddress} last={!(form.isStudent && livesOnCampus)}>
                <AddressBlock value={form.homeAddress} base="homeAddress" t={t} setField={setField} />
              </Subsection>
              {form.isStudent && livesOnCampus && (
                <Subsection title={t.schoolAddress} last>
                  <AddressBlock value={form.schoolAddress} base="schoolAddress" t={t} setField={setField} />
                </Subsection>
              )}
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.religiousHistoryTitle}</Typography>
            <TextField fullWidth sx={{ mb: 2 }} label={t.presentAffiliation} value={form.religiousHistory.presentAffiliation} onChange={(e) => setField('religiousHistory.presentAffiliation', e.target.value)} />
            <FormControl sx={{ mb: 1 }}>
              <FormLabel>{t.everBaptized}</FormLabel>
              <RadioGroup row value={form.religiousHistory.everBaptized} onChange={(e) => setField('religiousHistory.everBaptized', e.target.value)}>
                {t.baptizedOptions.map(o => <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />)}
              </RadioGroup>
            </FormControl>
            {form.religiousHistory.everBaptized === 'yes' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t.baptismDetails}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.denomination} value={form.religiousHistory.baptism.denomination} onChange={(e) => setField('religiousHistory.baptism.denomination', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.dateOrAge} value={form.religiousHistory.baptism.dateOrAge} onChange={(e) => setField('religiousHistory.baptism.dateOrAge', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.baptismalName} value={form.religiousHistory.baptism.baptismalName} onChange={(e) => setField('religiousHistory.baptism.baptismalName', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.churchName} value={form.religiousHistory.baptism.churchName} onChange={(e) => setField('religiousHistory.baptism.churchName', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.churchAddress} value={form.religiousHistory.baptism.churchAddress} onChange={(e) => setField('religiousHistory.baptism.churchAddress', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.location} value={form.religiousHistory.baptism.location} onChange={(e) => setField('religiousHistory.baptism.location', e.target.value)} /></Grid>
                </Grid>
                <Typography variant="body2" sx={{ mt: 2 }}>{t.catholicSacramentsPrompt}</Typography>
                <FormGroup row>
                  {t.sacramentOptions.map(o => (
                    <FormControlLabel key={o.value} control={<Checkbox checked={form.religiousHistory.catholicSacramentsReceived.includes(o.value)} onChange={() => toggleSacrament(o.value)} />} label={o.label} />
                  ))}
                </FormGroup>
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.maritalTitle}</Typography>
            <TextField select fullWidth label={t.maritalStatusLabel} value={status} onChange={(e) => setField('maritalStatus.status', e.target.value)}>
              {t.maritalStatusOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>

            {NEEDS_PARTNER.includes(status) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{status === 'engaged' ? t.fiance : t.spouse}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.partnerName} value={form.maritalStatus.partner.name} onChange={(e) => setField('maritalStatus.partner.name', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label={t.partnerAffiliation} value={form.maritalStatus.partner.affiliation} onChange={(e) => setField('maritalStatus.partner.affiliation', e.target.value)} /></Grid>
                </Grid>
              </Box>
            )}

            {NEEDS_MARRIAGE.includes(status) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t.marriageDetails}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="date" label={t.dateOfMarriage} InputLabelProps={{ shrink: true }} value={form.maritalStatus.marriage.dateOfMarriage} onChange={(e) => setField('maritalStatus.marriage.dateOfMarriage', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label={t.placeOfMarriage} value={form.maritalStatus.marriage.placeOfMarriage} onChange={(e) => setField('maritalStatus.marriage.placeOfMarriage', e.target.value)} /></Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select fullWidth label={t.officiatingAuthority} value={form.maritalStatus.marriage.officiatingAuthority} onChange={(e) => setField('maritalStatus.marriage.officiatingAuthority', e.target.value)}>
                      {t.officiatingOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </Box>
            )}

            {status === 'widowed' && (
              <Box sx={{ mt: 2 }}>
                <YesNoRadio t={t} label={t.widowedStillLiving} value={form.maritalStatus.widowedPreviousSpouseStillLiving} onChange={(v) => setField('maritalStatus.widowedPreviousSpouseStillLiving', v)} />
              </Box>
            )}

            {NEEDS_PREV_MARRIAGE_QS.includes(status) && (
              <Box sx={{ mt: 2 }}>
                <YesNoRadio t={t} label={t.registrantMarriedBefore} value={form.maritalStatus.registrant.marriedBefore} onChange={(v) => setField('maritalStatus.registrant.marriedBefore', v)} />
                {form.maritalStatus.registrant.marriedBefore && renderPreviousMarriages('registrant')}
                <Box sx={{ mt: 2 }}>
                  <YesNoRadio t={t} label={status === 'engaged' ? t.partnerMarriedBeforeFiance : t.partnerMarriedBeforeSpouse} value={form.maritalStatus.partner.marriedBefore} onChange={(v) => setField('maritalStatus.partner.marriedBefore', v)} />
                  {form.maritalStatus.partner.marriedBefore && renderPreviousMarriages('partner')}
                </Box>
              </Box>
            )}
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.faithJourneyTitle}</Typography>
            <Stack spacing={2}>
              <TextField fullWidth multiline minRows={2} label={t.q1} value={form.faithJourney.whatLedYou} onChange={(e) => setField('faithJourney.whatLedYou', e.target.value)} />
              <TextField fullWidth multiline minRows={2} label={t.q2} value={form.faithJourney.religiousEducationReceived} onChange={(e) => setField('faithJourney.religiousEducationReceived', e.target.value)} />
              <TextField fullWidth multiline minRows={2} label={t.q3} value={form.faithJourney.contactWithChurch} onChange={(e) => setField('faithJourney.contactWithChurch', e.target.value)} />
              <TextField fullWidth multiline minRows={2} label={t.q4} value={form.faithJourney.questionsConcerns} onChange={(e) => setField('faithJourney.questionsConcerns', e.target.value)} />
              <FormControl>
                <FormLabel>{t.intentPrompt}</FormLabel>
                <RadioGroup value={form.intent} onChange={(e) => setField('intent', e.target.value)} sx={{ gap: 1.5, mt: 0.5 }}>
                  <FormControlLabel sx={{ alignItems: 'flex-start' }} value="considering" control={<Radio sx={{ pt: 0 }} />} label={t.intentConsidering} />
                  <FormControlLabel sx={{ alignItems: 'flex-start' }} value="fairly-sure" control={<Radio sx={{ pt: 0 }} />} label={t.intentFairlySure(decisionDateStr)} />
                  <FormControlLabel sx={{ alignItems: 'flex-start' }} value="sure" control={<Radio sx={{ pt: 0 }} />} label={t.intentSure} />
                </RadioGroup>
              </FormControl>
            </Stack>
          </Box>
        );
      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.signatureTitle}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label={t.signatureName} value={form.signature.name} onChange={(e) => setField('signature.name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="date" label={t.signatureDate} InputLabelProps={{ shrink: true }} value={form.signature.date} onChange={(e) => setField('signature.date', e.target.value)} /></Grid>
            </Grid>
          </Box>
        );
      case 6:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>{t.paymentTitle}</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between"><Typography>{t.registrationFee}</Typography><Typography>${(Number(settings?.registrationFee) || 0).toFixed(2)}</Typography></Stack>
              {discountActive && (
                <Stack direction="row" justifyContent="space-between"><Typography color="success.main">{t.discountApplied}</Typography><Typography color="success.main">${(Number(settings?.discount?.discountedRegistrationFee) || 0).toFixed(2)}</Typography></Stack>
              )}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between"><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t.amountDue}</Typography><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>${amountDue.toFixed(2)}</Typography></Stack>
            </Paper>

            {amountDue < 1 ? (
              <Alert severity="info" sx={{ mb: 2 }}>{t.noFee}</Alert>
            ) : (
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>{t.howToPay}</FormLabel>
                <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <FormControlLabel value="online" control={<Radio />} label={t.payOnline} />
                  <FormControlLabel value="cash" control={<Radio />} label={t.payInPerson} />
                </RadioGroup>
              </FormControl>
            )}

            {amountDue >= 1 && paymentMethod === 'online' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t.cardInfo}</Typography>
                <Box id="ocia-reg-card-container" sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: 60 }} />
                {!squareReady && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">{t.loadingPaymentForm}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  // On a $0 fee, default the method to cash so submit is allowed without card.
  const submitDisabled = submitting || (amountDue >= 1 && !paymentMethod);

  return (
    <Box sx={{ bgcolor: '#eef2f6', minHeight: '100vh', py: { xs: 2, md: 5 } }}>
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 2 } }}>
        {/* Header banner */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
          <Box sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)', color: '#fff', px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Box sx={{ mb: 1.5 }}><LanguageToggle light t={t} language={language} setLang={setLang} /></Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>{formTitle}</Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.92, mt: 0.5 }}>{formSubtitle}</Typography>
          </Box>
        </Paper>

        {/* Progress */}
        <Paper elevation={1} sx={{ borderRadius: 3, p: { xs: 2, md: 2.5 }, mb: 3 }}>
          {!isMobile ? (
            <Stepper activeStep={activeStep} alternativeLabel>
              {t.steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          ) : (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>{t.steps[activeStep]}</Typography>
                <Typography variant="caption" color="text.secondary">{activeStep + 1} / {t.steps.length}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={((activeStep + 1) / t.steps.length) * 100} sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          )}
        </Paper>

        {/* Step content */}
        <Paper elevation={2} sx={{ borderRadius: 3, p: { xs: 2.5, md: 4 } }}>
          {showTestData && (
            <Box sx={{ mb: 2, textAlign: 'right' }}>
              <Button size="small" variant="outlined" color="secondary" onClick={fillTestData}>{t.fillTestData}</Button>
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {renderStep()}
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" justifyContent="space-between">
            <Button disabled={activeStep === 0 || submitting} onClick={handleBack} size="large">{t.back}</Button>
            {activeStep < PAYMENT_STEP ? (
              <Button variant="contained" size="large" onClick={handleNext}>{t.next}</Button>
            ) : (
              <Button variant="contained" color="success" size="large" onClick={handleSubmit} disabled={submitDisabled}>
                {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t.submit}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default OCIARegistration;
